import React, { useState } from "react";
import {
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Box, HStack, Text, Badge, Button, Collapse, VStack, Divider,
} from "@chakra-ui/react";
import { GRADE_COLORS } from "../../services/rlsAudit";

const GRADE_ORDER = ["red", "yellow", "grey", "green"];
const GRADE_LABEL = { green: "OK", yellow: "Partial", red: "Fail", grey: "Unreviewed" };

function Counts({ counts, testId }) {
  return (
    <HStack spacing={2} data-testid={testId}>
      {GRADE_ORDER.map((g) => (
        <Badge key={g} colorScheme={GRADE_COLORS[g]} fontSize="sm" px={2}>
          {counts[g] ?? 0}
        </Badge>
      ))}
    </HStack>
  );
}

/** PHI, or restricted for another reason (fax credentials, the audit log). */
function SensitivityBadge({ row }) {
  if (row.phi) {
    return <Badge colorScheme="red" variant="solid" fontSize="xs">PHI</Badge>;
  }
  if (row.intent === "restricted") {
    return <Badge colorScheme="orange" variant="solid" fontSize="xs">SENSITIVE</Badge>;
  }
  return null;   // ordinary reference data needs no label
}

function TableRow({ schema, row, onFix }) {
  const [open, setOpen] = useState(false);
  // Grey means unclassified — no manifest entry, so nothing can be generated.
  const fixable = row.grade !== "green" && row.grade !== "grey";

  return (
    <Box borderBottomWidth="1px" borderColor="gray.100" py={3}>
      <HStack
        justify="space-between"
        align="flex-start"
        cursor="pointer"
        onClick={() => setOpen((v) => !v)}
        data-testid={`row-${schema}-${row.table}`}
      >
        <Box flex="1" minW={0}>
          <HStack spacing={2} mb={0.5}>
            <Badge colorScheme={GRADE_COLORS[row.grade]} fontSize="xs">
              {GRADE_LABEL[row.grade]}
            </Badge>
            <Text fontSize="md" fontWeight="semibold">{row.table}</Text>
            <SensitivityBadge row={row} />
          </HStack>
          <Text fontSize="sm" color="gray.600">{row.reason}</Text>
        </Box>
        {fixable && (
          <Button
            size="sm"
            colorScheme="orange"
            variant="outline"
            flexShrink={0}
            data-testid={`fix-${schema}-${row.table}`}
            onClick={(e) => { e.stopPropagation(); onFix(schema, row.table); }}
          >
            Fix
          </Button>
        )}
      </HStack>

      <Collapse in={open} animateOpacity unmountOnExit>
        <VStack align="stretch" spacing={1.5} pl={2} pt={3} fontSize="sm">
          {row.intent_reason && (
            <Text color="gray.700">
              <b>Why {row.phi ? "PHI" : row.intent}:</b> {row.intent_reason}
            </Text>
          )}
          {row.permissive.length > 0 && (
            <Text color="red.600">
              <b>Grants everyone access:</b> {row.permissive.join(", ")}
            </Text>
          )}
          <Text><b>Existing policies:</b> {row.present.length ? row.present.join(", ") : "none"}</Text>
          {row.missing.length > 0 && (
            <Text color="gray.700">
              <b>Fix would add:</b> {row.missing.join(", ")}
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}

function SchemaPanel({ section, onFix }) {
  // Every table in the schema is listed, passing ones included — the report is
  // the record of what was checked, not just what failed. Alphabetical so a
  // given table sits in the same place on every scan and across schemas.
  const rows = section.tables
    .filter((t) => t.grade !== "absent")
    .sort((a, b) => a.table.localeCompare(b.table));

  return (
    <Box data-testid={`schema-${section.schema}`}>
      {rows.map((t) => (
        <TableRow key={t.table} schema={section.schema} row={t} onFix={onFix} />
      ))}
    </Box>
  );
}

export default function RlsReport({ report, onFix, onFixAll }) {
  return (
    <Box>
      <HStack mb={3} justify="space-between">
        <Text fontWeight="bold" fontSize="lg">Totals</Text>
        <HStack spacing={3}>
          <Counts counts={report.totals} testId="rls-totals" />
          <Button
            size="sm"
            colorScheme="orange"
            data-testid="fix-all-global"
            onClick={() => onFixAll(null)}
          >
            Fix All
          </Button>
        </HStack>
      </HStack>
      <Divider mb={2} />

      <Accordion allowMultiple>
        {report.schemas.map((s) => (
          <AccordionItem key={s.schema} borderColor="gray.200">
            <AccordionButton py={3}>
              <Box flex="1" textAlign="left" fontSize="md" fontWeight="semibold">
                {s.schema}
              </Box>
              <Counts counts={s.counts} />
              {/* as="span": AccordionButton renders a <button>, and nesting a
                  real <button> inside it is invalid HTML. */}
              <Button
                as="span"
                size="xs"
                colorScheme="orange"
                variant="outline"
                ml={3}
                data-testid={`fix-all-${s.schema}`}
                onClick={(e) => { e.stopPropagation(); onFixAll(s.schema); }}
              >
                Fix schema
              </Button>
              <AccordionIcon ml={2} />
            </AccordionButton>
            <AccordionPanel pb={4} pt={0} px={2}>
              <SchemaPanel section={s} onFix={onFix} />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
}
